import {
    readUserText,
    resolveUserAvatarUrl,
    resolveUserDisplayName,
    resolveUserUsername,
} from './userIdentity';

const ENTITY_TIMELINE_ITEM_KIND = 'ENTITY';
const ENTITY_TIMELINE_PAGE_LIMIT = 200;
const SUPPORTED_ENTITY_OBJECT_TYPES = Object.freeze(['EVENT', 'SESSION', 'VENUE']);
const SUPPORTED_ENTITY_OBJECT_TYPE_SET = new Set(SUPPORTED_ENTITY_OBJECT_TYPES);

function normalizeUpperString(value) {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim().toUpperCase();
}

function normalizeTimelineCount(value) {
    return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function normalizeOptionalTimelineCount(value) {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    return normalizeTimelineCount(value);
}

function normalizeAttendanceSummary(summary) {
    if (!summary || typeof summary !== 'object') {
        return null;
    }

    return {
        going: normalizeTimelineCount(summary.going),
        interested: normalizeTimelineCount(summary.interested),
        pending: normalizeTimelineCount(summary.pending),
    };
}

function normalizeAmenities(amenities) {
    if (!Array.isArray(amenities)) {
        return [];
    }

    return amenities
        .filter((amenity) => typeof amenity === 'string')
        .map((amenity) => amenity.trim())
        .filter(Boolean);
}

export function normalizeTimelineAuthor(author) {
    if (!author || typeof author !== 'object') {
        return null;
    }

    const id = readUserText(author.id);
    const username = resolveUserUsername(author);
    const displayName = resolveUserDisplayName(author);
    const profilePicture = resolveUserAvatarUrl(author);

    return {
        id: id || null,
        username: username || null,
        displayName: displayName || null,
        profilePicture: profilePicture || null,
    };
}

export function normalizeEntityObjectType(value) {
    const normalized = normalizeUpperString(value);
    return SUPPORTED_ENTITY_OBJECT_TYPE_SET.has(normalized) ? normalized : null;
}

export function normalizeEntityTimelineItem(item) {
    if (!item || typeof item !== 'object') {
        return null;
    }

    const objectType = normalizeEntityObjectType(item.objectType);
    if (!objectType) {
        return null;
    }

    return {
        ...item,
        objectType,
        author: normalizeTimelineAuthor(item.author),
        attendanceSummary: normalizeAttendanceSummary(item.attendanceSummary),
        organizerInviteCount: normalizeOptionalTimelineCount(item.organizerInviteCount),
        amenities: normalizeAmenities(item.amenities),
        timelineItemKind: ENTITY_TIMELINE_ITEM_KIND,
    };
}

export function normalizeEntityTimelineItems(entityItems) {
    return Array.isArray(entityItems)
        ? entityItems.map(normalizeEntityTimelineItem).filter(Boolean)
        : [];
}

export function normalizeEntityTimelinePage(page) {
    if (!Array.isArray(page)) {
        throw new Error('Event timeline response must be an array');
    }

    return page;
}

export {
    ENTITY_TIMELINE_ITEM_KIND,
    ENTITY_TIMELINE_PAGE_LIMIT,
    SUPPORTED_ENTITY_OBJECT_TYPES,
};
